import React, {useCallback, useMemo, useRef, useState} from 'react'
import {Element, ElementShapeType, TypedElement} from '../../entities/project'
import {Button as AntdButton, message, Row, Col} from 'antd'
import {CanvasToolManager, ToolType} from './toolLayers/CanvasToolManager'
import {NodeConfig} from 'konva/lib/Node'
import {ElementTypeId} from '../../entities/elementType'
import {Plant} from '../../entities/plant'
import {useNavigate, useRevalidator, useSubmit} from 'react-router-dom'
import {instance as plantsService} from '../../services/PlantsService'
import {ArrowDownOutlined, DeleteOutlined, DragOutlined} from '@ant-design/icons'
import {useIntl} from 'react-intl'
import './CanvasViewEditor.less'
import {CanvasViewEditorData, ElementWithVisibility} from './CanvasViewEditorData'
import '../viewProject/ViewProject.less'
import {Language} from '../../services/language'
import {useGardenPlanVariantParam} from '../../hooks/useGardenPlanVariantParam'

export type CanvasViewEditorProps = {
  projectId: string
  plotShape: Element
  elements: TypedElement[]
  editable: boolean
  projectPlants: Plant[]
  hasMap: boolean
  openPlantById: (id: number) => void
  // rendered in the button row, just left of the "add plant" button
  gardenPlanSelector?: React.ReactNode
  //  selectedElementIndex: number
  //  setSelectedElementIndex: (index: number) => void
}

export function CanvasViewEditor({
  projectId,
  plotShape,
  elements,
  editable,
  projectPlants,
  hasMap,
  openPlantById,
  gardenPlanSelector,
}: CanvasViewEditorProps) {
  const revalidator = useRevalidator()
  const [messageApi, contextHolder] = message.useMessage()
  const intl = useIntl()
  const language = intl.locale as Language
  const submit = useSubmit()
  const pendingPlantChange = useRef<{updatedElement: TypedElement} | null>(null)

  const [currentElementId, setCurrentElementId] = useState<number | null>(null)
  const [currentPlant, setCurrentPlant] = useState<{id: number; specimenIndex?: number} | null>(
    null
  )
  const [plantsHiddenRecord, setPlantsHiddenRecord] = useState<Record<number, boolean>>({})
  const [elementsHiddenRecord, setElementsHiddenRecord] = useState<Record<number, boolean>>({})

  const navigate = useNavigate()
  const {gardenPlanVariantId, withGardenPlanVariant} = useGardenPlanVariantParam()

  // main project plants can't be changed while a plan variant is active
  const lockedPlantIds = useMemo(
    () =>
      gardenPlanVariantId
        ? projectPlants.filter((plant) => !plant.gardenPlanVariantId).map((plant) => plant.id)
        : [],
    [gardenPlanVariantId, projectPlants]
  )
  const isCurrentPlantLocked = !!currentPlant && lockedPlantIds.includes(currentPlant.id)

  const [tool, setTool] = useState<ToolType>(null)
  const elementsPerType = new Map<ElementTypeId, TypedElement[]>()

  for (const element of elements) {
    const elementType = element.type
    const elementTypesArray = elementsPerType.get(elementType) || []
    elementTypesArray.push(element)
    elementsPerType.set(elementType, elementTypesArray)
  }

  const plantedProjectPlants = projectPlants.filter((plant) => plant.coordinates)

  const editCurrentPlantProps = useCallback(
    async (elementProps: Partial<NodeConfig>, specimenIndex: number | null) => {
      if (!currentPlant || lockedPlantIds.includes(currentPlant.id)) {
        return
      }

      const plant = projectPlants.find((plant) => plant.id === currentPlant.id)
      if (!plant) {
        return
      }

      const updatedElement = {
        x: elementProps.x,
        y: elementProps.y,
        width: elementProps.width,
        height: elementProps.height,
      }

      const result = await plantsService.editPlant(
        {
          ...plant,
          coordinates:
            typeof currentPlant.specimenIndex === 'number'
              ? plant.coordinates.map((coordinates, index) => {
                  if (index === currentPlant.specimenIndex) {
                    return updatedElement
                  } else {
                    return coordinates
                  }
                })
              : [updatedElement],
        },
        language
      )
      if (result.success) {
        submit(null, {method: 'post'})
      } else {
        messageApi.open({
          type: 'error',
          content: result.error,
        })
      }
    },
    [currentPlant?.id, currentPlant?.specimenIndex, projectPlants, lockedPlantIds]
  )

  async function digUpPlant(diggingPlantId?: number, diggingPlantSpecimenNumber?: number) {
    const result = await plantsService.removePlantFromCanvas(
      //diggingPlantId ? diggingPlantId : currentPlant.id,
      diggingPlantId,
      projectPlants.find((plant) => plant.id === diggingPlantId).coordinates,
      diggingPlantSpecimenNumber
    )
    setTool(null)

    if (result.success) {
      submit(null, {method: 'post'})
      revalidator.revalidate()
      setCurrentPlant(null)
    } else {
      messageApi.open({
        type: 'error',
        content: result.error?.message,
      })
    }
  }

  async function connectSolitariusTemplateToPlant(
    solitariusTemplateElement: ElementWithVisibility,
    plantId: string
  ) {
    const result = await plantsService.connectSolitariusTemplateToPlant(
      solitariusTemplateElement,
      parseInt(plantId, 10),
      projectId,
      projectPlants,
      elements
    )

    if (result.success) {
      submit(null, {method: 'post'})
      revalidator.revalidate()
    } else {
      messageApi.open({
        type: 'error',
        content: result.error,
      })
    }
  }

  async function plantPlant(plantingPlantId?: number, plantingPlantSpecimenNumber?: number) {
    //setCurrentElementId(null)
    setCurrentPlant({id: plantingPlantId, specimenIndex: plantingPlantSpecimenNumber})
    setTool('plant')
  }

  async function addPlantSpecimen(plantId: number, newSpecimenCoordinates?: NodeConfig) {
    const result = await plantsService.addPlantSpecimen(
      plantId,
      projectId,
      projectPlants.find((plant) => plant.id === plantId).coordinates
    )
    setTool(null)

    if (result.success) {
      submit(null, {method: 'post'})
      revalidator.revalidate()
    } else {
      messageApi.open({
        type: 'error',
        content: result.error?.message,
      })
    }
  }

  async function duplicatePlant(
    duplicatingPlantId: number,
    specimenNumber: number,
    specimenQuantity: number,
    direction?: string
  ) {
    const oldCoordinates = projectPlants.find(
      (plant) => plant.id === duplicatingPlantId
    ).coordinates
    const duplicatingSpecimenCoordinates = oldCoordinates[specimenNumber]
    const newCoordinates = []

    for (let i = 0; i < specimenQuantity; i++) {
      newCoordinates.push({
        ...duplicatingSpecimenCoordinates,
        x: duplicatingSpecimenCoordinates.x + duplicatingSpecimenCoordinates.width,
      })
    }

    const result = await plantsService.addPlantSpecimen(
      duplicatingPlantId,
      projectId,
      oldCoordinates,
      newCoordinates
    )
    setTool(null)

    if (result.success) {
      submit(null, {method: 'post'})
      revalidator.revalidate()
      setCurrentPlant({
        id: currentPlant.id,
        specimenIndex: oldCoordinates.length + newCoordinates.length - 1,
      })
    } else {
      messageApi.open({
        type: 'error',
        content: result.error?.message,
      })
    }
  }

  async function removePlantSpecimen(plantId: number, plantSpecimenNumber: number) {
    const result = await plantsService.removePlantSpecimen(
      plantId,
      projectId,
      projectPlants.find((plant) => plant.id === plantId).coordinates,
      plantSpecimenNumber
    )
    setTool(null)

    if (result.success) {
      submit(null, {method: 'post'})
      revalidator.revalidate()
    } else {
      messageApi.open({
        type: 'error',
        content: result.error?.message,
      })
    }
  }

  return (
    <>
      {contextHolder}

      <div className="view-project__plant-buttons">
        <AntdButton
          disabled={!currentPlant || isCurrentPlantLocked}
          onClick={async () => {
            setTool('plant')
          }}
        >
          <DragOutlined />
          {projectPlants.find((plant) => plant.id === currentPlant?.id)?.coordinates.length > 0 ? (
            <>{intl.formatMessage({id: 'replantPlant'})}</>
          ) : (
            <>{intl.formatMessage({id: 'plantPlant'})}</>
          )}
        </AntdButton>

        <AntdButton
          disabled={!currentPlant || isCurrentPlantLocked}
          onClick={() => digUpPlant(currentPlant.id)}
        >
          <DeleteOutlined /> {intl.formatMessage({id: 'digUpPlant'})}
        </AntdButton>

        {gardenPlanSelector}

        <AntdButton
          id="button-no-todos"
          className="add-plant-button"
          onClick={() => {
            navigate(withGardenPlanVariant(`/projects/${projectId}/plants/new`))
          }}
          style={
            projectPlants.length > 0
              ? {color: '#f67d4e'}
              : {backgroundColor: '#f67d4e', color: 'white'}
          }
        >
          <ArrowDownOutlined style={{fontWeight: '700'}} />
          {projectPlants.length > 0
            ? intl.formatMessage({id: 'addPlant'})
            : intl.formatMessage({id: 'createTheFirstPlantInTheProject'})}
        </AntdButton>
      </div>

      <div className="view-project__canvas">
        <CanvasToolManager
          canvasEditorProps={{
            projectId: projectId,
            plotShape: plotShape,
            elements: elements.filter((element) => !elementsHiddenRecord[element.elementId]),
            plants: plantedProjectPlants.filter((plant) => !plantsHiddenRecord[plant.id]),
            selectable: true,
            plotShapeEditable: false,
            editableTypes: ['plant'],
            lockedPlantIds,
            openPlantById,
            selectedElementId: currentElementId,
            setSelectedElementId: setCurrentElementId,
            digUpPlant: digUpPlant,
            duplicatePlant: duplicatePlant,
            selectedPlant: currentPlant
              ? {
                  ...currentPlant,
                  name: projectPlants.find((plant) => plant.id === currentPlant?.id)?.name,
                  // coordinates: currentPlant?.specimenIndex
                  //   ? projectPlants.find((plant) => plant.id === currentPlant?.id).coordinates[
                  //       currentPlant.specimenIndex
                  //     ]
                  //   : projectPlants.find((plant) => plant.id === currentPlant?.id)?.coordinates[0],
                }
              : null,
            setSelectedPlant: setCurrentPlant,
            onElementChanged: (updatedElement: TypedElement) => {
              pendingPlantChange.current = {updatedElement}
            }, //onElementChangedDebounced,
            hasMap,
            activeDraggableElement: null,
            async onMouseUp() {
              if (pendingPlantChange.current) {
                const element = pendingPlantChange.current.updatedElement
                if (element.type !== 'plant') {
                  return
                }

                await editCurrentPlantProps(element.props, element.specimenIndex)
              }

              pendingPlantChange.current = null
            },
          }}
          tool={tool}
          onElementDrawn={async function (shape: {type: ElementShapeType; props: NodeConfig}) {
            if (tool !== 'plant') {
              setTool(null)
              return
            }

            await editCurrentPlantProps(shape.props, null)

            setTool(null)
          }}
        />
      </div>
      <div className="view-project__canvas-data">
        <CanvasViewEditorData
          // another project opened from the menu reuses this page, so start its panel afresh
          key={projectId}
          elements={elements.map((element) => ({
            ...element,
            isVisible: !elementsHiddenRecord[element.elementId],
          }))}
          projectPlants={projectPlants.map((plant) => ({
            ...plant,
            isVisible: !plantsHiddenRecord[plant.id],
          }))}
          setPlantVisible={(plantId, isVisible) => {
            setPlantsHiddenRecord((current) => ({...current, [plantId]: !isVisible}))
          }}
          setElementVisible={(elementId, isVisible) => {
            setElementsHiddenRecord((current) => ({...current, [elementId]: !isVisible}))
          }}
          currentPlant={currentPlant}
          setCurrentPlant={setCurrentPlant}
          currentElementId={currentElementId}
          setCurrentElementId={setCurrentElementId}
          openPlantById={openPlantById}
          setTool={setTool}
          digUpPlant={digUpPlant}
          plantPlant={plantPlant}
          addPlantSpecimen={addPlantSpecimen}
          removePlantSpecimen={removePlantSpecimen}
          connectSolitariusTemplateToPlant={connectSolitariusTemplateToPlant}
          lockedPlantIds={lockedPlantIds}
        ></CanvasViewEditorData>
      </div>
    </>
  )
}
